import { supabase } from '../lib/supabase';
import { submissionService } from './submissionService';
import { profileService } from './profileService';
import { conferenceService } from './conferenceService';
import { resolveBadgeRole, badgeRoleLabel } from '../lib/badge/badgeRole';
import { formatDate } from '../utils/formatters';

export const badgeService = {
  /**
   * conference_roles for this user in this conference — chair/organizer/
   * reviewer. Authors aren't tracked there; accepted-submission status
   * covers that case separately (see hasAcceptedSubmission).
   */
  async getConferenceRoles(conferenceId, userId) {
    const { data, error } = await supabase
      ?.from('conference_roles')
      ?.select('role')
      ?.eq('conference_id', conferenceId)
      ?.eq('user_id', userId);

    if (error) throw error;
    return (data || [])?.map((r) => r?.role)?.filter(Boolean);
  },

  async hasAcceptedSubmission(conferenceId, userId) {
    const submissions = await submissionService?.getUserSubmissions(conferenceId, userId);
    return (submissions || [])?.some((s) => s?.status === 'accepted');
  },

  /**
   * Whether this user gets a badge for this conference, and as what role.
   * Returns null when not eligible — a submitted-but-not-yet-accepted
   * author with no other role, for instance.
   */
  async getEligibility(conferenceId, userId) {
    const [roles, hasAccepted] = await Promise.all([
      this.getConferenceRoles(conferenceId, userId),
      this.hasAcceptedSubmission(conferenceId, userId),
    ]);

    const role = resolveBadgeRole(roles, hasAccepted);
    if (!role) return null;
    return { role, roleLabel: badgeRoleLabel(role) };
  },

  /**
   * Everything renderBadgePdf needs, fetched and assembled. Getting or
   * creating the participant number happens here, lazily — the first
   * eligible person (of any role) to reach this for a given conference is
   * the one who triggers it, never a batch job. Throws if the caller isn't
   * eligible; check getEligibility first if you want to show/hide UI
   * instead of handling the throw.
   */
  async getBadgeData(conferenceId, userId) {
    const eligibility = await this.getEligibility(conferenceId, userId);
    if (!eligibility) {
      throw new Error('Not eligible for a badge in this conference.');
    }

    const [conference, profileResult, participantNumber] = await Promise.all([
      conferenceService?.getById(conferenceId),
      profileService?.getProfile(userId),
      submissionService?.getOrCreateConferenceParticipantNumber(conferenceId, userId),
    ]);

    // conference.bannerImageUrl is a storage PATH, not a fetchable URL — same
    // as everywhere else the banner is shown (dashboard preview, landing page).
    const bannerSignedUrl = conference?.bannerImageUrl
      ? await conferenceService?.getBannerImageUrl(conference.bannerImageUrl)
      : null;

    const profile = profileResult?.data;
    const participantId = submissionService?.formatConferenceParticipantId(
      conference?.shortName || conference?.name,
      participantNumber
    );

    const dateRange = conference?.startDate
      ? conference?.endDate && conference.endDate !== conference.startDate
        ? `${formatDate(conference.startDate)} – ${formatDate(conference.endDate)}`
        : formatDate(conference.startDate)
      : '';

    return {
      attendee: {
        name: `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() || profile?.email || '',
        institution: profile?.institution || '',
        participantId,
      },
      roleLabel: eligibility.roleLabel,
      conference: {
        name: conference?.name,
        bannerImageUrl: bannerSignedUrl,
        dateRange,
      },
      settings: {
        headerText: conference?.badgeHeaderText,
        showInstitution: conference?.badgeShowInstitution ?? true,
        showQr: conference?.badgeShowQr ?? true,
      },
    };
  },
};

export default badgeService;
